<template>
  <div class="d-flex flex-column ga-4">
        <div class="d-flex justify-space-between align-center">
            <div>
                <div class="text-subtitle-1 font-weight-bold">
                    Roles da aplicacao
                </div>
                <div class="text-body-2 text-medium-emphasis">
                    {{ accessName }}
                </div>
            </div>

            <v-tooltip text="Criar Role na aplicacao">
                <template #activator="{ props: tooltipProps }">
                    <v-btn
                        v-bind="tooltipProps"
                        icon="mdi-plus"
                        color="info"
                        variant="flat"
                        :disabled="isSubmitting"
                        @click="openCreateRoleDialog"
                    />
                </template>
            </v-tooltip>
        </div>

        <v-data-table-server
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            :headers="appRoleManagementTableHeaders"
            :items="appSpecificRoles"
            :items-length="totalRolesInApp"
            :loading="isFetchingAppDetails || isFetchingAppSpecificRoles"
            :items-per-page-options="[5, 10, 15, 20]"
            item-value="id"
            @update:options="fetchAppSpecificRoles"
        >
            <template #item.permissions="{ item }">
                {{ formatPermissions(item.permissions) }}
            </template>

            <template #item.actions="{ item }">
                <div class="d-flex ga-2">
                    <v-tooltip text="Editar Role">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-pencil"
                                color="info"
                                variant="text"
                                :disabled="isSubmitting"
                                @click="openEditRoleDialog(item)"
                            />
                        </template>
                    </v-tooltip>

                    <v-tooltip text="Apagar Role">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                icon="mdi-trash-can"
                                color="error"
                                variant="text"
                                :disabled="isSubmitting"
                                @click="openDeleteRoleDialog(item)"
                            />
                        </template>
                    </v-tooltip>
                </div>
            </template>

            <template #no-data>
                <div class="empty-state">
                    <h4>Sem roles</h4>
                    <p>Nao existem roles configurados para esta aplicacao.</p>
                </div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useDialog } from "@internal_projects_dd/universal-dialog";
import AccessRoleForm from "@/components/accesses/AccessRoleForm.vue";
import Accesses from "@/api/Accesses";
import AccessRoles from "@/api/AccessRoles";

const props = defineProps({
    access: {
        type: Object,
        default: () => ({}),
    },
    accessId: {
        type: String,
        default: "",
    },
    options: {
        type: Object,
        default: () => ({}),
    },
});

const dialog = useDialog();
const isSubmitting = ref(false);
const isFetchingAppDetails = ref(false);
const isFetchingAppSpecificRoles = ref(false);
const accessDetails = ref({});
const appSpecificRoles = ref([]);
const totalRolesInApp = ref(0);
const page = ref(props.options.page || 1);
const itemsPerPage = ref(props.options.itemsPerPage || 10);

const appRoleManagementTableHeaders = [
    {
        title: "Código",
        key: "name",
        sortable: true,
    },
    {
        title: "Nome",
        key: "display_name",
        sortable: true,
    },
    {
        title: "Descrição",
        key: "description",
        sortable: false,
    },
    {
        title: "Permissões",
        key: "permissions",
        sortable: false,
    },
    {
        title: "Acções",
        key: "actions",
        sortable: false,
        width: 120,
    },
];

const accessName = computed(() => {
    return accessDetails.value?.name || props.access?.name || resolvedAccessId.value || "Aplicacao";
});

const resolvedAccessId = computed(() => {
    return String(props.accessId || props.access?.id || accessDetails.value?.id || "").trim();
});

onMounted(async () => {
    await fetchAppDetails();
});

watch(
    () => props.accessId,
    async () => {
        await fetchAppDetails();
        await fetchAppSpecificRoles({
            page: 1,
            itemsPerPage: itemsPerPage.value,
        });
    },
);

async function fetchAppDetails() {
    const accessId = resolvedAccessId.value;

    if (!accessId) {
        accessDetails.value = {};
        return null;
    }

    if (props.access?.id === accessId && props.access?.name) {
        accessDetails.value = props.access;
        return accessDetails.value;
    }

    isFetchingAppDetails.value = true;

    try {
        const response = await Accesses.get(accessId);
        accessDetails.value = response?.data ?? {};
        return accessDetails.value;
    } catch (error) {
        console.error("Erro ao carregar os detalhes do acesso:", error?.response?.data || error);
        accessDetails.value = props.access ?? {};
        return accessDetails.value;
    } finally {
        isFetchingAppDetails.value = false;
    }
}

async function fetchAppSpecificRoles(params = {}) {
    const accessId = resolvedAccessId.value;

    if (!accessId) {
        appSpecificRoles.value = [];
        totalRolesInApp.value = 0;
        return;
    }

    isFetchingAppSpecificRoles.value = true;

    try {
        const nextPage = params.page || page.value;
        const nextItemsPerPage = params.itemsPerPage || itemsPerPage.value;

        page.value = nextPage;
        itemsPerPage.value = nextItemsPerPage;

        const response = await AccessRoles.list(accessId, params);
        const items = normalizeRolesResponse(response?.data);

        appSpecificRoles.value = items;
        totalRolesInApp.value = response?.data?.total || items.length;
    } catch (error) {
        console.error("Erro ao obter os roles da aplicacao:", error?.response?.data || error);
        appSpecificRoles.value = [];
        totalRolesInApp.value = 0;
    } finally {
        isFetchingAppSpecificRoles.value = false;
    }
}

function normalizeRolesResponse(payload) {
    const source = Array.isArray(payload)
        ? payload
        : payload?.data ?? payload ?? [];
    const roles = Array.isArray(source)
        ? source
        : Object.values(source);

    return roles
        .map((role) => ({
            id: role?.id || "",
            name: role?.name || "",
            display_name: role?.display_name || role?.name || "",
            description: role?.description || "",
            permissions: Array.isArray(role?.permissions) ? role.permissions : [],
        }))
        .filter((role) => role.name);
}

function formatPermissions(permissions = []) {
    if (!Array.isArray(permissions) || permissions.length === 0) {
        return "-";
    }

    return permissions
        .map((permission) => {
            if (typeof permission === "string") {
                return permission;
            }

            return permission?.display_name || permission?.name;
        })
        .filter(Boolean)
        .join("; ");
}

async function openCreateRoleDialog() {
    await openRoleDialog("create");
}

async function openEditRoleDialog(role) {
    await openRoleDialog("edit", role);
}

async function openRoleDialog(mode, role = {}) {
    const actionLabel = mode === "create" ? "criar" : "editar";

    await dialog.open({
        component: AccessRoleForm,
        props: {
            mode,
            access: accessDetails.value,
            accessId: resolvedAccessId.value,
            role,
            readonlyInputs: {
                access_id: true,
            },
            disabledInputs: {
                // access_id: true,
            },
        },
        options: {
            title: `${mode === "create" ? "Criar" : "Editar"} role para aplicacao: ${accessName.value}`,
            titleBackgroundColor: "white",
            titleTextColor: "black",
            bodyText: "Ocorreu um erro a montar o componente.",
            maxWidth: "960px",
            confirmActionText: mode === "create" ? "Criar" : "Atualizar",
            confirmActionIcon: "",
            confirmActionVariant: "text",
            confirmActionColor: "info",
            cancelActionText: "Cancelar",
            cancelActionVariant: "text",
            cancelActionColor: "black",
            cancelActionIcon: "",
            onConfirm: "submit",
            onConfirmActionText: mode === "create" ? "Criando..." : "Atualizando...",
            onConfirmSuccess: async () => {
                fetchAppSpecificRoles({
                    page: page.value,
                    itemsPerPage: itemsPerPage.value,
                });
                await openSuccessDialog(
                    "Sucesso",
                    `Role ${mode === "create" ? "criado" : "atualizado"} com sucesso!`,
                );
            },
            onConfirmError: (error, currentDialog) => {
                currentDialog.options.onConfirmFailed = true;
                currentDialog.options.onConfirmFailedErrorTitle = `Erro a ${actionLabel} o role.`;
                currentDialog.options.onConfirmFailedErrorMessage = error?.response?.data?.message || error?.message || String(error);
            },
            onConfirmFailureThrow: true,
            onConfirmFailureExpectedResult: false,
        },
    });
}

async function openDeleteRoleDialog(role = {}) {
    await dialog.open({
        component: AccessRoleForm,
        props: {
            mode: "delete",
            access: accessDetails.value,
            accessId: resolvedAccessId.value,
            role,
            readonly: true,
            readonlyInputs: {
                access_id: true,
                name: true,
                display_name: true,
                description: true,
                permissions: true,
            },
            disabledInputs: {
                // access_id: true,
                // name: true,
                // display_name: true,
                // description: true,
                // permissions: true,
            },
        },
        options: {
            title: `Apagar role da aplicacao: ${accessName.value}`,
            titleBackgroundColor: "white",
            titleTextColor: "black",
            bodyText: "Ocorreu um erro a montar o componente.",
            maxWidth: "960px",
            confirmActionText: "Apagar",
            confirmActionIcon: "",
            confirmActionVariant: "text",
            confirmActionColor: "error",
            cancelActionText: "Cancelar",
            cancelActionVariant: "text",
            cancelActionColor: "black",
            cancelActionIcon: "",
            onConfirmActionText: "Apagando...",
            onConfirm: async () => {
                const confirmed = await openDeleteConfirmationDialog(role);

                if (!confirmed) {
                    return false;
                }

                return await AccessRoles.delete(resolvedAccessId.value, role.id);
            },
            onConfirmSuccess: async () => {
                fetchAppSpecificRoles({
                    page: page.value,
                    itemsPerPage: itemsPerPage.value,
                });
                await openSuccessDialog("Sucesso", "Role apagado com sucesso!");
            },
            onConfirmError: (error, currentDialog) => {
                currentDialog.options.onConfirmFailed = true;
                currentDialog.options.onConfirmFailedErrorTitle = "Erro a apagar o role.";
                currentDialog.options.onConfirmFailedErrorMessage = error?.response?.data?.message || error?.message || String(error);
            },
            onConfirmFailureThrow: false,
            onConfirmFailureExpectedResult: false,
        },
    });
}

async function openDeleteConfirmationDialog(role = {}) {
    return await dialog.open({
        options: {
            title: "Confirmar eliminação",
            bodyText: `Tem a certeza que pretende apagar o role "${role?.display_name || role?.name || "sem nome"}"?`,
            persistent: true,
            maxWidth: 520,
            confirmActionText: "Confirmar",
            confirmActionColor: "error",
            confirmActionIcon: "",
            cancelActionText: "Cancelar",
            cancelActionColor: "black",
            cancelActionVariant: "text",
            cancelActionIcon: "",
        },
    });
}

async function openSuccessDialog(title, bodyText) {
    await dialog.open({
        options: {
            title,
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}
</script>

<style scoped>
.empty-state {
    text-align: center;
    padding: 32px 16px;
}
</style>
